import { create } from "zustand";
import type {
  AppId,
  WindowState,
  ActivityEvent,
  ActivityKind,
  NotificationItem,
  ByoncPolicy,
  CodeWorkspace,
  HiveTask,
  HiveIntegration,
  IntegrationCatalogEntry,
  IntegrationCredentials,
  Toast,
} from "@/types";
import { BUILTIN_BYONC_POLICIES } from "@/data/workspaces";
import { APPS } from "@/data/apps";
import { api, ApiError, apiErrorMessage } from "@/api/client";
import type { EntitlementMap, Me, Paged, ProductId, Role, SampleDataStatus } from "@/api/types";
import { clamp, uid } from "@/lib/utils";
import { isProductApp, productState, PRODUCT_IDS } from "@/lib/entitlements";
import { externalAppUrl, openExternalApp } from "@/lib/appLinks";

type Theme = "dark" | "light";
type DataStatus = "idle" | "loading" | "ready" | "error";

const KNOWN_KINDS: ActivityKind[] = [
  "verification",
  "specialization",
  "routing",
  "job",
  "agent",
  "integration",
];

interface ServerActivityItem {
  id: string;
  title: string;
  detail: string;
  time: string;
  kind: string;
}

const mapActivity = (a: ServerActivityItem): ActivityEvent => ({
  id: a.id,
  title: a.title,
  detail: a.detail,
  time: a.time,
  kind: (KNOWN_KINDS.includes(a.kind as ActivityKind) ? a.kind : "job") as ActivityKind,
});

interface HiveState {
  theme: Theme;
  toggleTheme: () => void;

  /* ── Server data lifecycle ──────────────────────────────── */
  dataStatus: DataStatus;
  hydrate: () => Promise<void>;
  refreshActivity: () => Promise<void>;

  /* ── Session-scoped gating (mirrors /api/auth/me — the single source) ── */
  entitlements: EntitlementMap | null;
  sampleData: SampleDataStatus | null;
  meUserId: string | null;
  /** Active-org role — drives admin-only surfaces like OAuth app setup. */
  meRole: Role | null;
  setSession: (me: Me | null) => void;

  /* ── Product preview / demand capture ───────────────────── */
  /** Product whose preview modal is open (locked/preview tiles never dead-end). */
  previewProduct: ProductId | null;
  openProductPreview: (p: ProductId | null) => void;
  /** Products the current user / the org has requested early access for. */
  earlyAccessMine: Partial<Record<ProductId, string>>;
  earlyAccessOrg: Partial<Record<ProductId, number>>;
  loadEarlyAccess: () => Promise<void>;
  requestEarlyAccess: (product: ProductId) => Promise<void>;

  /* ── Sample data lifecycle ──────────────────────────────── */
  seedSampleData: () => Promise<void>;
  deleteSampleData: () => Promise<void>;
  setSampleVisibility: (visible: boolean) => Promise<void>;

  windows: WindowState[];
  topZ: number;
  openApp: (appId: AppId) => void;
  closeWindow: (id: string) => void;
  focusWindow: (id: string) => void;
  minimizeWindow: (id: string) => void;
  toggleMaximize: (id: string) => void;
  restoreWindow: (id: string) => void;
  moveWindow: (id: string, x: number, y: number) => void;
  resizeWindow: (id: string, w: number, h: number, x: number, y: number) => void;

  paletteOpen: boolean;
  setPalette: (v: boolean) => void;

  activityOpen: boolean;
  setActivity: (v: boolean) => void;

  notifOpen: boolean;
  setNotif: (v: boolean) => void;

  activity: ActivityEvent[];
  pushActivity: (e: Omit<ActivityEvent, "id" | "time">) => void;

  notifications: NotificationItem[];
  pushNotification: (n: Omit<NotificationItem, "id" | "time" | "read">) => void;
  markAllRead: () => void;
  dismissNotif: (id: string) => void;

  /* ── Toasts ─────────────────────────────────────────────── */
  toasts: Toast[];
  pushToast: (t: Omit<Toast, "id">) => void;
  dismissToast: (id: string) => void;

  /* ── Workspaces (cloud dev environments) ────────────────── */
  codeWorkspaces: CodeWorkspace[];
  activeWorkspaceId: string;
  selectedWorkspaceId: string | null;
  wsWizardOpen: boolean;
  setActiveWorkspace: (id: string) => void;
  selectWorkspace: (id: string | null) => void;
  setWsWizard: (v: boolean) => void;
  workspaceAction: (
    id: string,
    action: "open" | "resume" | "sleep" | "verify" | "sync" | "clone" | "archive" | "unarchive"
  ) => void;
  createWorkspace: (partial: Partial<CodeWorkspace>, opts?: { linkTaskId?: string }) => void;
  restoreSnapshot: (workspaceId: string, snapshotId: string) => void;

  /* ── BYONC routing policies (built-in modes ⊕ org-created) ── */
  byoncPolicies: ByoncPolicy[];
  loadByoncPolicies: () => Promise<void>;

  /* ── Tasks ──────────────────────────────────────────────── */
  tasks: HiveTask[];
  selectedTaskId: string | null;
  selectTask: (id: string | null) => void;
  refineTask: (id: string) => void;
  sendTaskToAres: (id: string, workspaceId: string) => void;
  resolveQuestion: (taskId: string, qId: string, status: "resolved" | "ignored") => void;
  setTaskStatus: (id: string, status: HiveTask["status"]) => void;
  askViaSlack: (taskId: string, qId: string) => void;

  autoResolveLevel: number;
  setAutoResolveLevel: (n: number) => void;

  /* ── Integrations ───────────────────────────────────────── */
  integrations: HiveIntegration[];
  selectedIntegrationId: string | null;
  selectIntegration: (id: string | null) => void;
  connectIntegration: (id: string, credentials?: IntegrationCredentials) => Promise<ConnectOutcome>;
  /** Real OAuth via self-hosted Nango: opens the popup and polls to completion. */
  connectOAuth: (id: string) => Promise<OAuthConnectOutcome>;
  disconnectIntegration: (id: string) => void;
  reauthIntegration: (id: string) => void;
  requestAccess: (id: string, reason: string, note: string) => void;
  /** Sweep every live integration with a real connectivity test. */
  testAllIntegrations: () => Promise<void>;
  testSweep: { done: number; total: number; failed: number } | null;

  /* ── Connector catalogue ("Connect App") ────────────────── */
  catalog: IntegrationCatalogEntry[];
  catalogStatus: DataStatus;
  /** Deployment-level OAuth infrastructure (drives the admin setup panel). */
  catalogOauth: { enabled: boolean; callbackUrl: string | null };
  connectCatalogOpen: boolean;
  /** Provider to preselect when the catalogue opens (deep link from a card). */
  connectCatalogTarget: string | null;
  setConnectCatalog: (v: boolean, targetId?: string | null) => void;
  loadCatalog: () => Promise<void>;
  installIntegration: (
    catalogId: string,
    credentials?: IntegrationCredentials
  ) => Promise<ConnectOutcome>;
  testIntegration: (id: string) => Promise<void>;
  /** Admin one-time setup: everything needed to create the provider OAuth app. */
  fetchOAuthApp: (id: string) => Promise<OAuthAppInfo | null>;
  /** Admin: save the provider OAuth app (client id/secret) — enables Connect for everyone. */
  setupOAuthApp: (id: string, clientId: string, clientSecret: string, scopes?: string) => Promise<ConnectOutcome>;
}

/** GET /integrations/:id/oauth-app — admin setup panel payload. */
export interface OAuthAppInfo {
  enabled: boolean;
  supported: boolean;
  configured: boolean;
  providerKey: string;
  clientId?: string | null;
  scopes?: string;
  recommendedScopes?: string;
  callbackUrl?: string;
  appUrl?: string;
}

/**
 * Result of an install/connect attempt, rich enough for the connect form to
 * show the provider's actual answer inline (and stay open for a correction).
 */
export interface ConnectOutcome {
  ok: boolean;
  /** Human-facing reason when not ok (provider rejection, API error…). */
  error?: string;
  integration?: HiveIntegration;
}

export interface OAuthConnectOutcome extends ConnectOutcome {
  /** The org's inbound event endpoint for this provider (auto-provisioned). */
  inboundHook?: { created: boolean; url: string | null; webhookId: string };
  /** Whether Hive registered the provider's webhooks automatically, or the
   *  user must paste the endpoint URL into the provider's settings. */
  providerWebhooks?: { mode: "auto" | "manual"; detail: string };
}

const BAR_H = 52;
const DOCK_H = 96;

/** Refresh every server-backed store after sample-data changes. Dynamic imports
 *  keep this free of module-level cycles (those stores import useHive). */
async function refreshAllStores(get: () => HiveState): Promise<void> {
  const [{ useWorkflow }, { useExecution }] = await Promise.all([
    import("@/store/workflowStore"),
    import("@/store/executionStore"),
  ]);
  await Promise.all([get().hydrate(), useWorkflow.getState().hydrate(), useExecution.getState().hydrate()]);
}

function spawnPosition(index: number, w: number, h: number) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const baseX = clamp(vw / 2 - w / 2 + (index % 5) * 28 - 56, 24, vw - w - 24);
  const baseY = clamp(
    BAR_H + 28 + (index % 5) * 26,
    BAR_H + 12,
    Math.max(BAR_H + 12, vh - DOCK_H - h - 12)
  );
  return { x: baseX, y: baseY };
}

export const useHive = create<HiveState>((set, get) => {
  // Persist a workspace change to the server and reconcile local state.
  const persistWorkspace = (id: string, body: unknown) =>
    api
      .post<CodeWorkspace>(`/api/v1/workspaces/${id}/actions`, body)
      .then((updated) =>
        set((s) => ({ codeWorkspaces: s.codeWorkspaces.map((w) => (w.id === id ? updated : w)) }))
      )
      .catch(() =>
        get().pushToast({ title: "Sync failed", body: "Change wasn't saved — retry", tone: "error" })
      );

  const replaceTask = (id: string, updated: HiveTask) =>
    set((s) => ({ tasks: s.tasks.map((t) => (t.id === id ? updated : t)) }));
  const replaceIntegration = (id: string, updated: HiveIntegration) =>
    set((s) => ({ integrations: s.integrations.map((i) => (i.id === id ? updated : i)) }));
  const syncError = () =>
    get().pushToast({ title: "Sync failed", body: "Change wasn't saved — retry", tone: "error" });

  return {
    theme: "dark",
    toggleTheme: () =>
      set((s) => {
        const theme = s.theme === "dark" ? "light" : "dark";
        document.documentElement.classList.toggle("dark", theme === "dark");
        return { theme };
      }),

    /* ── Server data ─────────────────────────────────────── */
    dataStatus: "idle",
    hydrate: async () => {
      if (get().dataStatus === "loading") return;
      // Silent when already ready (sample-data toggles, org refreshes) — the
      // full-screen loader is for first paint only.
      if (get().dataStatus !== "ready") set({ dataStatus: "loading" });
      try {
        const [ws, ts, ints, act] = await Promise.all([
          api.get<Paged<CodeWorkspace>>("/api/v1/workspaces?limit=100&sort=created_at&order=asc"),
          api.get<Paged<HiveTask>>("/api/v1/tasks?limit=100&sort=created_at&order=asc"),
          api.get<Paged<HiveIntegration>>("/api/v1/integrations?limit=500"),
          api.get<{ items: ServerActivityItem[] }>("/api/v1/activity"),
        ]);
        set((s) => ({
          codeWorkspaces: ws.items,
          activeWorkspaceId: ws.items.some((w) => w.id === s.activeWorkspaceId)
            ? s.activeWorkspaceId
            : ws.items[0]?.id ?? "",
          tasks: ts.items,
          integrations: ints.items,
          activity: act.items.slice(0, 40).map(mapActivity),
          dataStatus: "ready",
        }));
        // Non-critical side load — the wizard falls back to built-in modes.
        void get().loadByoncPolicies();
      } catch {
        if (get().dataStatus !== "ready") set({ dataStatus: "error" });
      }
    },

    /* ── Session gating mirror ───────────────────────────── */
    entitlements: null,
    sampleData: null,
    meUserId: null,
    meRole: null,
    setSession: (me) =>
      set({
        entitlements: me?.entitlements ?? null,
        sampleData: me?.sampleData ?? null,
        meUserId: me?.user.id ?? null,
        meRole: me?.role ?? null,
      }),

    /* ── Product preview / demand capture ────────────────── */
    previewProduct: null,
    openProductPreview: (p) => set({ previewProduct: p }),
    earlyAccessMine: {},
    earlyAccessOrg: {},
    loadEarlyAccess: async () => {
      try {
        const res = await api.get<{
          items: { product: ProductId; userId: string; requestedAt: string }[];
        }>("/api/v1/early-access");
        const mine: Partial<Record<ProductId, string>> = {};
        const org: Partial<Record<ProductId, number>> = {};
        const myId = get().meUserId;
        for (const item of res.items) {
          if (!PRODUCT_IDS.includes(item.product)) continue;
          org[item.product] = (org[item.product] ?? 0) + 1;
          if (myId && item.userId === myId) mine[item.product] = item.requestedAt;
        }
        set({ earlyAccessMine: mine, earlyAccessOrg: org });
      } catch {
        /* non-critical — buttons just show the unrequested state */
      }
    },
    requestEarlyAccess: async (product) => {
      try {
        const res = await api.post<{ requested: boolean; requestedAt: string | null; alreadyRequested: boolean }>(
          "/api/v1/early-access",
          { product },
        );
        set((s) => ({
          earlyAccessMine: { ...s.earlyAccessMine, [product]: res.requestedAt ?? "now" },
          earlyAccessOrg: {
            ...s.earlyAccessOrg,
            [product]: (s.earlyAccessOrg[product] ?? 0) + (res.alreadyRequested ? 0 : 1),
          },
        }));
        if (!res.alreadyRequested) {
          get().pushToast({
            title: "You're on the list",
            body: `We'll reach out about ${APPS[product].name} early access`,
            tone: "success",
          });
        }
      } catch {
        get().pushToast({ title: "Request failed", body: "Could not record your request — retry", tone: "error" });
      }
    },

    /* ── Sample data lifecycle ───────────────────────────── */
    seedSampleData: async () => {
      try {
        const status = await api.post<SampleDataStatus>("/api/v1/sample-data");
        set({ sampleData: status });
        get().pushToast({
          title: "Sample data loaded",
          body: "Explore Hive with a realistic demo dataset — everything is clearly labelled",
          tone: "success",
        });
        await refreshAllStores(get);
      } catch {
        get().pushToast({ title: "Couldn't load sample data", body: "Retry from Settings → Sample data", tone: "error" });
      }
    },
    deleteSampleData: async () => {
      try {
        const status = await api.del<SampleDataStatus>("/api/v1/sample-data");
        set({ sampleData: status });
        get().pushToast({ title: "Sample data deleted", body: "All sample records were permanently removed", tone: "info" });
        await refreshAllStores(get);
      } catch (err) {
        const detail = err instanceof ApiError && err.status === 403
          ? "Requires an org admin"
          : "Retry from Settings → Sample data";
        get().pushToast({ title: "Couldn't delete sample data", body: detail, tone: "error" });
      }
    },
    setSampleVisibility: async (visible) => {
      try {
        const status = await api.patch<SampleDataStatus>("/api/v1/sample-data/visibility", { visible });
        set({ sampleData: status });
        await refreshAllStores(get);
      } catch {
        get().pushToast({ title: "Couldn't update visibility", body: "Retry from Settings → Sample data", tone: "error" });
      }
    },
    refreshActivity: async () => {
      try {
        const act = await api.get<{ items: ServerActivityItem[] }>("/api/v1/activity");
        set({ activity: act.items.slice(0, 40).map(mapActivity) });
      } catch {
        /* transient — the next poll retries */
      }
    },

    windows: [],
    topZ: 10,

    openApp: (appId) => {
      const openWindow = () =>
        set((s) => {
          const existing = s.windows.find((w) => w.appId === appId);
          const topZ = s.topZ + 1;
          if (existing) {
            return {
              topZ,
              windows: s.windows.map((w) =>
                w.id === existing.id ? { ...w, z: topZ, minimized: false } : w
              ),
            };
          }
          const def = APPS[appId];
          const w = Math.min(def.defaultSize.w, window.innerWidth - 48);
          const h = Math.min(def.defaultSize.h, window.innerHeight - BAR_H - DOCK_H - 24);
          const { x, y } = spawnPosition(s.windows.length, w, h);
          const win: WindowState = {
            id: uid("win"),
            appId,
            x,
            y,
            w,
            h,
            z: topZ,
            minimized: false,
            maximized: false,
          };
          return { topZ, windows: [...s.windows, win] };
        });

      // Entitlement gate for product apps (states come from /api/auth/me):
      //   hidden  → not rendered anywhere, so nothing to do;
      //   locked  → preview modal only (never a dead end);
      //   preview → in-desktop Early-Access preview window, never the real app;
      //   enabled → the real product (external tab when configured).
      if (isProductApp(appId)) {
        const state = productState(get().entitlements, appId);
        if (state === "hidden") return;
        if (state === "locked") {
          set({ previewProduct: appId });
          return;
        }
        if (state === "preview") {
          openWindow();
          return;
        }
      }

      // Real product configured for this app? Launch it (SSO via Hive) instead
      // of the in-desktop mock window. Fallback preserved when env is unset.
      const external = externalAppUrl(appId);
      if (external) {
        openExternalApp(external);
        get().pushToast({
          title: `Opening ${APPS[appId].name}`,
          body: "Launching in a new tab — signed in via Hive",
          tone: "info",
        });
        return;
      }
      openWindow();
    },

    closeWindow: (id) => set((s) => ({ windows: s.windows.filter((w) => w.id !== id) })),

    focusWindow: (id) =>
      set((s) => {
        const topZ = s.topZ + 1;
        return {
          topZ,
          windows: s.windows.map((w) => (w.id === id ? { ...w, z: topZ, minimized: false } : w)),
        };
      }),

    minimizeWindow: (id) =>
      set((s) => ({
        windows: s.windows.map((w) => (w.id === id ? { ...w, minimized: true } : w)),
      })),

    toggleMaximize: (id) =>
      set((s) => {
        const topZ = s.topZ + 1;
        return {
          topZ,
          windows: s.windows.map((w) => {
            if (w.id !== id) return w;
            if (w.maximized) {
              return { ...w, maximized: false, z: topZ, ...(w.prev ?? {}) };
            }
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            return {
              ...w,
              maximized: true,
              z: topZ,
              prev: { x: w.x, y: w.y, w: w.w, h: w.h },
              x: 16,
              y: BAR_H + 12,
              w: vw - 32,
              h: vh - BAR_H - DOCK_H - 16,
            };
          }),
        };
      }),

    restoreWindow: (id) => get().focusWindow(id),

    moveWindow: (id, x, y) =>
      set((s) => ({ windows: s.windows.map((w) => (w.id === id ? { ...w, x, y } : w)) })),

    resizeWindow: (id, w, h, x, y) =>
      set((s) => ({
        windows: s.windows.map((win) => (win.id === id ? { ...win, w, h, x, y } : win)),
      })),

    paletteOpen: false,
    setPalette: (v) => set({ paletteOpen: v }),

    activityOpen: false,
    setActivity: (v) => set({ activityOpen: v }),

    notifOpen: false,
    setNotif: (v) => set({ notifOpen: v }),

    activity: [],
    pushActivity: (e) =>
      set((s) => ({
        activity: [
          { ...e, id: uid("evt"), time: "now" },
          ...s.activity.map((a) => (a.time === "now" ? { ...a, time: "1m" } : a)),
        ].slice(0, 40),
      })),

    notifications: [],
    pushNotification: (n) =>
      set((s) => ({
        notifications: [
          { ...n, id: uid("ntf"), time: "now", read: false },
          ...s.notifications,
        ].slice(0, 30),
      })),
    markAllRead: () =>
      set((s) => ({ notifications: s.notifications.map((n) => ({ ...n, read: true })) })),
    dismissNotif: (id) => set((s) => ({ notifications: s.notifications.filter((n) => n.id !== id) })),

    /* ── Toasts ───────────────────────────────────────────── */
    toasts: [],
    pushToast: (t) =>
      set((s) => ({ toasts: [...s.toasts, { ...t, id: uid("toast") }].slice(-4) })),
    dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

    /* ── Workspaces ───────────────────────────────────────── */
    codeWorkspaces: [],
    activeWorkspaceId: "",
    selectedWorkspaceId: null,
    wsWizardOpen: false,
    setActiveWorkspace: (id) => {
      const ws = get().codeWorkspaces.find((w) => w.id === id);
      set({ activeWorkspaceId: id });
      if (ws) {
        get().pushToast({ title: "Workspace switched", body: `${ws.repo} · ${ws.branch}`, tone: "info" });
      }
    },
    selectWorkspace: (id) => set({ selectedWorkspaceId: id }),
    setWsWizard: (v) => set({ wsWizardOpen: v }),
    workspaceAction: (id, action) => {
      if (!get().codeWorkspaces.some((w) => w.id === id)) return;
      set((s) => {
        const ws = s.codeWorkspaces.find((w) => w.id === id);
        if (!ws) return {};
        let status = ws.status;
        let archived = ws.archived ?? false;
        let toast: Omit<Toast, "id"> | null = null;
        switch (action) {
          case "open":
            status = "active";
            break;
          case "resume":
            status = "active";
            toast = { title: "Workspace resumed", body: `${ws.repo} is now active`, tone: "success" };
            break;
          case "sleep":
            status = "sleeping";
            toast = { title: "Workspace sleeping", body: `${ws.repo} paused to save compute`, tone: "info" };
            break;
          case "verify":
            status = "verifying";
            toast = { title: "Verification started", body: `ARES is verifying ${ws.branch}`, tone: "info" };
            break;
          case "sync":
            status = "active";
            toast = { title: "Branch synced", body: `${ws.branch} is up to date with ${ws.baseBranch}`, tone: "success" };
            break;
          case "clone":
            toast = { title: "Workspace cloned", body: `Created a copy of ${ws.name}`, tone: "success" };
            break;
          case "archive":
            archived = true;
            status = "sleeping";
            toast = { title: "Workspace archived", body: `${ws.name} moved to the Archived tab — unarchive it any time`, tone: "info" };
            break;
          case "unarchive":
            archived = false;
            status = "sleeping";
            toast = { title: "Workspace restored", body: `${ws.name} is back in your workspaces`, tone: "success" };
            break;
        }
        if (toast) {
          get().pushToast(toast);
          get().pushActivity({ kind: "agent", title: toast.title, detail: `${ws.repo} · ${ws.branch}`, app: "workspaces" });
        }
        if (action === "sync")
          get().pushNotification({ title: "Workspace synced", body: `${ws.repo} synced with ${ws.baseBranch}.`, tone: "success", app: "workspaces" });
        const codeWorkspaces = s.codeWorkspaces.map((w) =>
          w.id === id ? { ...w, status, archived, commitsBehind: action === "sync" ? 0 : w.commitsBehind } : w
        );
        // An archived workspace leaves the default views — drop selection/active.
        const next: Partial<HiveState> = { codeWorkspaces };
        if (action === "archive") {
          if (s.selectedWorkspaceId === id) next.selectedWorkspaceId = null;
          if (s.activeWorkspaceId === id)
            next.activeWorkspaceId = codeWorkspaces.find((w) => !w.archived)?.id ?? "";
        }
        return next;
      });
      // Persist (clone returns a new workspace to prepend).
      if (action === "clone") {
        api
          .post<CodeWorkspace>(`/api/v1/workspaces/${id}/actions`, { action })
          .then((created) => set((s) => ({ codeWorkspaces: [created, ...s.codeWorkspaces] })))
          .catch(syncError);
      } else {
        void persistWorkspace(id, { action });
      }
    },
    createWorkspace: (partial, opts) => {
      // Workspaces are files + an IDE — they always link to ARES.
      const product = (partial.product as AppId) || "ares";
      const accent = APPS[product].accent;
      const draft: Omit<CodeWorkspace, "id"> = {
        name: partial.name || "New Workspace",
        repo: partial.repo || "new-repo",
        provider: partial.provider || "github",
        branch: partial.branch || "main",
        baseBranch: partial.baseBranch || "main",
        product,
        env: partial.env || "cloud",
        status: "active",
        lastOpened: "now",
        lastVerified: "—",
        openTasks: partial.openTasks ?? 0,
        activeAgents: 0,
        lang: partial.lang || "TypeScript",
        accent: partial.accent || accent,
        commitsBehind: 0,
        garlic: partial.garlic || "available",
        byonc: partial.byonc || "cost-optimized",
        integrations: partial.integrations || ["github"],
        usage: { disk: "0 GB", runtime: "0m", cpu: 0, mem: 0 },
        secretsProfile: partial.secretsProfile || "default",
        compute: partial.compute || "4 vCPU · 16 GB",
        persistent: partial.persistent ?? true,
        autoVerify: partial.autoVerify ?? true,
        aresAgents: partial.aresAgents ?? true,
        ...(partial.localPath ? { localPath: partial.localPath } : {}),
        ...(partial.s3Bucket ? { s3Bucket: partial.s3Bucket } : {}),
        ...(partial.s3Prefix ? { s3Prefix: partial.s3Prefix } : {}),
        commits: [],
        agents: [],
        verifications: [],
        timeline: partial.timeline || [
          { label: "Workspace created", detail: `${partial.repo} · ${partial.branch}`, time: "now" },
        ],
        snapshots: partial.snapshots || [],
        suggestions: [],
        health: { build: "—", tests: "—", deps: "up to date" },
      };
      set({ wsWizardOpen: false });
      api
        .post<CodeWorkspace>("/api/v1/workspaces", draft)
        .then((ws) => {
          set((s) => ({ codeWorkspaces: [ws, ...s.codeWorkspaces], activeWorkspaceId: ws.id }));
          get().pushToast({ title: "Workspace created", body: `${ws.repo} · ${ws.branch}`, tone: "success" });
          get().pushNotification({ title: "Workspace created", body: `${ws.name} is ready.`, tone: "success", app: "workspaces" });
          get().pushActivity({ kind: "agent", title: "Workspace created", detail: ws.name, app: "workspaces" });
          // Branch-from-ticket / branch-from-task: bind the task to its new workspace.
          const taskId = opts?.linkTaskId;
          if (taskId && get().tasks.some((t) => t.id === taskId)) {
            set((s) => ({ tasks: s.tasks.map((t) => (t.id === taskId ? { ...t, workspaceId: ws.id } : t)) }));
            api
              .patch<HiveTask>(`/api/v1/tasks/${taskId}`, { workspaceId: ws.id })
              .then((u) => replaceTask(taskId, u))
              .catch(syncError);
          }
        })
        .catch(() => get().pushToast({ title: "Create failed", body: "Could not create the workspace", tone: "error" }));
    },
    /* ── BYONC routing policies ───────────────────────────── */
    byoncPolicies: BUILTIN_BYONC_POLICIES,
    loadByoncPolicies: async () => {
      try {
        const res = await api.get<{ items: ByoncPolicy[] }>("/api/v1/byonc/policies");
        if (res.items.length) set({ byoncPolicies: res.items });
      } catch {
        /* keep the built-in fallback — the wizard still works */
      }
    },

    restoreSnapshot: (workspaceId, snapshotId) => {
      const ws = get().codeWorkspaces.find((w) => w.id === workspaceId);
      const snap = ws?.snapshots.find((sn) => sn.id === snapshotId);
      if (snap) {
        get().pushToast({ title: "Snapshot restored", body: `"${snap.name}" restored`, tone: "success" });
        get().pushNotification({ title: "Snapshot restored", body: `Restored "${snap.name}" on ${ws!.repo}.`, tone: "success", app: "workspaces" });
        get().pushActivity({ kind: "agent", title: "Snapshot restored", detail: snap.name, app: "workspaces" });
      }
    },

    /* ── Tasks ────────────────────────────────────────────── */
    tasks: [],
    selectedTaskId: null,
    selectTask: (id) => set({ selectedTaskId: id }),
    refineTask: (id) => {
      const t = get().tasks.find((x) => x.id === id);
      if (!t) return;
      const hasQuestions = t.questions.some((q) => q.status === "open");
      get().pushToast({ title: "Task refined", body: `${t.id} prepared for ARES`, tone: "success" });
      get().pushNotification({ title: "Task refined", body: `${t.id} — ${t.title} was refined for ARES.`, tone: "success", app: "tasks" });
      get().pushActivity({ kind: "job", title: "Task refined", detail: `${t.id} · ${t.title}`, app: "tasks" });
      set((s) => ({
        tasks: s.tasks.map((x) =>
          x.id === id
            ? {
                ...x,
                refined: true,
                status: hasQuestions ? "needs-clarification" : "ready",
                scores: {
                  ...x.scores,
                  clarity: Math.max(x.scores.clarity, 84),
                  readiness: Math.max(x.scores.readiness, hasQuestions ? 62 : 88),
                  verification: Math.max(x.scores.verification, 80),
                },
              }
            : x
        ),
      }));
      api.post<HiveTask>(`/api/v1/tasks/${id}/refine`).then((u) => replaceTask(id, u)).catch(syncError);
    },
    sendTaskToAres: (id, workspaceId) => {
      const t = get().tasks.find((x) => x.id === id);
      const ws = get().codeWorkspaces.find((w) => w.id === workspaceId);
      if (!t) return;
      get().pushToast({ title: "Sent to ARES", body: `${t.id} assigned${ws ? ` · ${ws.repo}` : ""}`, tone: "success" });
      get().pushNotification({ title: "Task sent to ARES", body: `${t.id} is queued for implementation${ws ? ` in ${ws.name}` : ""}.`, tone: "success", app: "ares" });
      get().pushActivity({ kind: "agent", title: "Task sent to ARES", detail: `${t.id} → ${ws?.repo ?? "workspace"}`, app: "ares" });
      set((s) => ({
        tasks: s.tasks.map((x) => (x.id === id ? { ...x, status: "assigned", workspaceId, assignee: "ARES" } : x)),
      }));
      api.post<HiveTask>(`/api/v1/tasks/${id}/send-to-ares`, { workspaceId }).then((u) => replaceTask(id, u)).catch(syncError);
    },
    resolveQuestion: (taskId, qId, status) => {
      let nextQuestions: HiveTask["questions"] = [];
      set((s) => ({
        tasks: s.tasks.map((t) => {
          if (t.id !== taskId) return t;
          nextQuestions = t.questions.map((q) => (q.id === qId ? { ...q, status } : q));
          return { ...t, questions: nextQuestions };
        }),
      }));
      api.patch<HiveTask>(`/api/v1/tasks/${taskId}`, { questions: nextQuestions }).then((u) => replaceTask(taskId, u)).catch(syncError);
    },
    askViaSlack: (taskId, qId) => {
      const t = get().tasks.find((x) => x.id === taskId);
      get().resolveQuestion(taskId, qId, "resolved");
      if (t) {
        get().pushToast({ title: "Slack question sent", body: `Asked ${t.reporter} about ${t.id}`, tone: "info" });
        get().pushNotification({ title: "Slack question sent", body: `Clarification for ${t.id} sent to ${t.reporter}.`, tone: "info", app: "tasks" });
        get().pushActivity({ kind: "integration", title: "Slack question sent", detail: `${t.id} → ${t.reporter}`, app: "integrations" });
      }
    },
    setTaskStatus: (id, status) => {
      set((s) => ({ tasks: s.tasks.map((t) => (t.id === id ? { ...t, status } : t)) }));
      api.post<HiveTask>(`/api/v1/tasks/${id}/status`, { status }).then((u) => replaceTask(id, u)).catch(syncError);
    },

    autoResolveLevel: 1,
    setAutoResolveLevel: (n) => set({ autoResolveLevel: clamp(n, 0, 4) }),

    /* ── Integrations ─────────────────────────────────────── */
    integrations: [],
    selectedIntegrationId: null,
    selectIntegration: (id) => set({ selectedIntegrationId: id }),
    connectIntegration: async (id, credentials) => {
      const it = get().integrations.find((i) => i.id === id);
      if (!it) return { ok: false, error: "Integration not found" };
      try {
        const updated = await api.post<HiveIntegration>(`/api/v1/integrations/${id}/actions`, {
          action: "connect",
          credentials,
        });
        replaceIntegration(id, updated);
        if (updated.status === "connected") {
          get().pushToast({ title: "Integration connected", body: `${it.name} is now connected`, tone: "success" });
          get().pushNotification({ title: "Integration connected", body: `${it.name} connected successfully.`, tone: "success", app: "integrations" });
          get().pushActivity({ kind: "integration", title: "Connected", detail: it.name, app: "integrations" });
          return { ok: true, integration: updated };
        }
        const reason = updated.lastError?.message ?? `${it.name} rejected the credential — check it and retry`;
        get().pushToast({ title: "Credential rejected", body: reason, tone: "error" });
        return { ok: false, error: reason, integration: updated };
      } catch (err) {
        syncError();
        return { ok: false, error: err instanceof ApiError ? err.message : "Could not reach the Hive API — retry" };
      }
    },
    connectOAuth: async (id) => {
      // The connector must exist in the org before OAuth can bind to it.
      const cat = get().catalog.find((c) => c.id === id);
      if (cat && !cat.installed) {
        const installed = await get().installIntegration(id, undefined);
        if (!installed.ok && !installed.integration) return installed;
      }
      const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
      let popup: Window | null = null;
      try {
        const start = await api.post<{
          sessionToken: string;
          expiresAt: string;
          connectUrl: string;
          startedAt: string;
        }>(`/api/v1/integrations/${id}/connect/start`);
        popup = window.open(start.connectUrl, "hive-oauth-connect", "width=520,height=740,popup=yes");
        if (!popup) return { ok: false, error: "The OAuth popup was blocked — allow popups for Hive and retry" };

        // Poll the server until it sees the finished connection on Nango. The
        // popup is cross-origin, so polling is the only completion signal.
        const deadline = Date.now() + 5 * 60_000;
        let closedGrace = 2; // callbacks can land moments after the window closes
        while (Date.now() < deadline) {
          await sleep(2500);
          if (popup.closed && closedGrace-- < 0) {
            return { ok: false, error: "The OAuth window was closed before finishing — try again" };
          }
          try {
            const res = await api.post<{
              integration: HiveIntegration;
              inboundHook: { created: boolean; url: string | null; webhookId: string };
              providerWebhooks?: { mode: "auto" | "manual"; detail: string };
            }>(`/api/v1/integrations/${id}/connect/complete`, { startedAt: start.startedAt });
            try {
              popup.close();
            } catch {
              /* popup may already be gone */
            }
            replaceIntegration(id, res.integration);
            set({ selectedIntegrationId: id });
            get().pushToast({ title: "Connected via OAuth", body: `${res.integration.name} is live`, tone: "success" });
            get().pushNotification({ title: "Integration connected", body: `${res.integration.name} connected with OAuth.`, tone: "success", app: "integrations" });
            get().pushActivity({ kind: "integration", title: "Connected via OAuth", detail: res.integration.name, app: "integrations" });
            return { ok: true, integration: res.integration, inboundHook: res.inboundHook, providerWebhooks: res.providerWebhooks };
          } catch (err) {
            if (err instanceof ApiError && err.code === "oauth_pending") continue;
            return { ok: false, error: err instanceof ApiError ? apiErrorMessage(err) : "Could not reach the Hive API — retry" };
          }
        }
        return { ok: false, error: "The OAuth flow timed out — try again" };
      } catch (err) {
        try {
          popup?.close();
        } catch {
          /* ignore */
        }
        return { ok: false, error: err instanceof ApiError ? apiErrorMessage(err) : "Could not start the OAuth flow — retry" };
      }
    },
    disconnectIntegration: (id) => {
      const it = get().integrations.find((i) => i.id === id);
      if (!it) return;
      get().pushToast({ title: "Integration disconnected", body: `${it.name} disconnected`, tone: "warning" });
      set((s) => ({
        integrations: s.integrations.map((i) =>
          i.id === id ? { ...i, status: "not-connected", health: "healthy", lastSync: "—" } : i
        ),
      }));
      api.post<HiveIntegration>(`/api/v1/integrations/${id}/actions`, { action: "disconnect" }).then((u) => replaceIntegration(id, u)).catch(syncError);
    },
    reauthIntegration: (id) => {
      const it = get().integrations.find((i) => i.id === id);
      if (!it) return;
      get().pushToast({ title: "Re-authorized", body: `${it.name} re-authenticated`, tone: "success" });
      get().pushNotification({ title: "Integration re-authorized", body: `${it.name} is healthy again.`, tone: "success", app: "integrations" });
      get().pushActivity({ kind: "integration", title: "Re-authorized", detail: it.name, app: "integrations" });
      set((s) => ({
        integrations: s.integrations.map((i) =>
          i.id === id ? { ...i, status: "connected", health: "healthy", lastSync: "now" } : i
        ),
      }));
      api.post<HiveIntegration>(`/api/v1/integrations/${id}/actions`, { action: "reauth" }).then((u) => replaceIntegration(id, u)).catch(syncError);
    },
    requestAccess: (id, reason, note) => {
      const it = get().integrations.find((i) => i.id === id);
      if (!it) return;
      get().pushToast({ title: "Access requested", body: `Request sent to admin for ${it.name}`, tone: "info" });
      get().pushNotification({ title: "Admin access requested", body: `Requested ${it.name}: ${reason}${note ? ` — "${note}"` : ""}.`, tone: "info", app: "integrations" });
      get().pushActivity({ kind: "integration", title: "Access requested", detail: `${it.name} · ${reason}`, app: "integrations" });
      set((s) => ({ integrations: s.integrations.map((i) => (i.id === id ? { ...i, requested: true } : i)) }));
      api.post<HiveIntegration>(`/api/v1/integrations/${id}/request-access`, { reason, note }).then((u) => replaceIntegration(id, u)).catch(syncError);
    },

    /* ── Connector catalogue ──────────────────────────────── */
    catalog: [],
    catalogStatus: "idle",
    catalogOauth: { enabled: false, callbackUrl: null },
    connectCatalogOpen: false,
    connectCatalogTarget: null,
    setConnectCatalog: (v, targetId = null) =>
      set({ connectCatalogOpen: v, connectCatalogTarget: v ? targetId : null }),
    loadCatalog: async () => {
      if (get().catalogStatus === "loading") return;
      set({ catalogStatus: "loading" });
      try {
        const res = await api.get<{
          items: IntegrationCatalogEntry[];
          oauth?: { enabled: boolean; callbackUrl: string | null };
        }>("/api/v1/integrations/catalog");
        set({
          catalog: res.items,
          catalogOauth: res.oauth ?? { enabled: false, callbackUrl: null },
          catalogStatus: "ready",
        });
      } catch {
        set({ catalogStatus: "error" });
      }
    },
    fetchOAuthApp: async (id) => {
      try {
        return await api.get<OAuthAppInfo>(`/api/v1/integrations/${id}/oauth-app`);
      } catch {
        return null;
      }
    },
    setupOAuthApp: async (id, clientId, clientSecret, scopes) => {
      try {
        await api.put<{ ok: boolean }>(`/api/v1/integrations/${id}/oauth-app`, {
          clientId,
          clientSecret,
          ...(scopes?.trim() ? { scopes: scopes.trim() } : {}),
        });
        const name = get().catalog.find((c) => c.id === id)?.name ?? id;
        get().pushToast({ title: "OAuth app saved", body: `${name} can now be connected with OAuth`, tone: "success" });
        get().pushActivity({ kind: "integration", title: "OAuth app registered", detail: name, app: "integrations" });
        // Refresh so oauthReady flips for every open surface.
        set({ catalogStatus: "idle" });
        await get().loadCatalog();
        return { ok: true };
      } catch (err) {
        return { ok: false, error: err instanceof ApiError ? apiErrorMessage(err) : "Could not save the OAuth app — retry" };
      }
    },
    installIntegration: async (catalogId, credentials) => {
      try {
        const res = await api.post<{
          integration: HiveIntegration;
          test: { ok: boolean; latencyMs?: number; detail: string } | null;
        }>("/api/v1/integrations", { catalogId, credentials });
        const it = res.integration;
        set((s) => ({
          integrations: [it, ...s.integrations.filter((i) => i.id !== it.id)],
          selectedIntegrationId: it.id,
          catalog: s.catalog.map((c) => (c.id === catalogId ? { ...c, installed: true } : c)),
        }));
        const rejected = it.status === "needs-reauth";
        const title =
          it.status === "connected"
            ? "Integration connected"
            : rejected
              ? "Credential rejected"
              : "Installed — credentials pending";
        get().pushToast({
          title,
          body: res.test?.detail ?? `${it.name} added to your organization`,
          tone: it.status === "connected" ? "success" : rejected ? "error" : "info",
        });
        get().pushNotification({ title: "Connector installed", body: `${it.name} was added from the catalogue.`, tone: "success", app: "integrations" });
        get().pushActivity({ kind: "integration", title: "Connector installed", detail: it.name, app: "integrations" });
        // A rejected credential is a failed connect: keep the form open so the
        // user can correct it in place (the connector itself is installed).
        if (rejected) {
          return { ok: false, error: res.test?.detail ?? "The provider rejected the credential", integration: it };
        }
        return { ok: true, integration: it };
      } catch (err) {
        const reason = err instanceof ApiError ? err.message : "Could not install the connector";
        get().pushToast({ title: "Install failed", body: reason, tone: "error" });
        return { ok: false, error: reason };
      }
    },
    testSweep: null,
    testAllIntegrations: async () => {
      const targets = get().integrations.filter(
        (i) => i.status === "connected" && !i.admin && i.isSample !== true,
      );
      if (targets.length === 0) {
        get().pushToast({
          title: "Nothing to test",
          body: "No live integrations are connected yet — sample data stays inert",
          tone: "info",
        });
        return;
      }
      set({ testSweep: { done: 0, total: targets.length, failed: 0 } });
      let failed = 0;
      for (const it of targets) {
        try {
          const res = await api.post<{ ok: boolean; integration: HiveIntegration }>(
            `/api/v1/integrations/${it.id}/test`,
          );
          replaceIntegration(it.id, res.integration);
          if (!res.ok) failed += 1;
        } catch {
          failed += 1;
        }
        set((s) => (s.testSweep ? { testSweep: { ...s.testSweep, done: s.testSweep.done + 1, failed } } : {}));
      }
      set({ testSweep: null });
      get().pushToast({
        title: failed === 0 ? "All connections healthy" : `${failed} of ${targets.length} checks failed`,
        body:
          failed === 0
            ? `${targets.length} integration${targets.length > 1 ? "s" : ""} verified live`
            : "Failing cards show the provider's answer — open one to fix it",
        tone: failed === 0 ? "success" : "error",
      });
    },
    testIntegration: async (id) => {
      const it = get().integrations.find((i) => i.id === id);
      if (!it) return;
      try {
        const res = await api.post<{
          ok: boolean;
          latencyMs?: number;
          detail: string;
          integration: HiveIntegration;
        }>(`/api/v1/integrations/${id}/test`);
        replaceIntegration(id, res.integration);
        get().pushToast({
          title: res.ok ? "Connection healthy" : "Connection failed",
          body: res.detail,
          tone: res.ok ? "success" : "error",
        });
      } catch {
        get().pushToast({ title: "Test failed", body: "Could not reach the Hive API", tone: "error" });
      }
    },
  };
});
